import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  fetchVideoById,
  updateVideo,
  fetchSpecialties,
  fetchSpecialtyAttributeKeys
} from '../../services/videoApi';
import { SessionManager } from '../../utils/SessionManager';
import { resolveSpecialtyAttributes } from '../../utils/specialtyAttributes';
import type {
  Video as ApiVideo,
  Specialty as ApiSpecialty,
  SpecialtyAttribute,
  VideoUpdateReq
} from '../../services/videoApi/videoApi.types';
import {
  fetchVideoImages,
  createVideoImage,
  deleteVideoImage
} from '../../services/videoImageApi';
import { useVideoEditImages } from '../../hooks/useVideoEditImages';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { SnackbarType } from '../../components/Snackbar';
import { useConfirmDialog } from '../../contexts/ConfirmDialogProvider';
import { convertBmpToPng, isBmpFile } from '../../utils/imageFormat';
import { UseVideoEditReturn, UsePromptReturn, Video, VideoImage, Specialty } from './types';

/**
 * APIから返されるビデオデータをコンポーネントで使用するVideo型に変換する
 * @param apiVideo APIから返されるビデオデータ
 * @returns コンポーネントで使用するVideo型のデータ
 */
const mapApiVideoToVideo = (apiVideo: ApiVideo): Video => {
  return {
    id: apiVideo.id,
    title: apiVideo.title || '',
    description: apiVideo.description,
    visibility: apiVideo.visibility,
    tags: apiVideo.tags,
    created_at: apiVideo.created_at,
    published_at: apiVideo.published_at,
    specialty: apiVideo.specialty ? {
      id: String(apiVideo.specialty.id), // 必ずstring型に変換
      name: apiVideo.specialty.name
    } : undefined,
    specialty_attributes: apiVideo.specialty_attributes
  };
};

/**
 * 保存前にキー付きタグを整える。キーか値が空の行は捨てる
 */
const normalizeSpecialtyAttributes = (attributes: SpecialtyAttribute[]): SpecialtyAttribute[] =>
  attributes
    .map(attribute => ({
      attribute_key: attribute.attribute_key.trim(),
      attribute_value: attribute.attribute_value.trim()
    }))
    .filter(attribute => attribute.attribute_key && attribute.attribute_value);

/**
 * 非公開から公開範囲を広げるときは診療科が必須。
 * 公開された動画は他の人が診療科で探す対象になるため、未分類のまま公開させない
 */
export const PUBLISH_REQUIRES_SPECIALTY_MESSAGE = '公開範囲を変更するには診療科の設定が必要です';

export const requiresSpecialtyForVisibilityChange = (
  previousVisibility: string,
  nextVisibility: string,
  specialtyId: string
): boolean => nextVisibility !== 'private' && nextVisibility !== previousVisibility && !specialtyId;

/**
 * APIから返される診療科データをコンポーネントで使用するSpecialty型に変換する
 * @param apiSpecialties APIから返される診療科データの配列
 * @returns コンポーネントで使用するSpecialty型の配列
 */
const mapApiSpecialtiesToSpecialties = (apiSpecialties: ApiSpecialty[]): Specialty[] => {
  return apiSpecialties.map(specialty => ({
    id: String(specialty.id), // 必ずstring型に変換
    name: specialty.name
  }));
};

// ページ遷移をブロックするカスタムフック
export const usePrompt = (when: boolean, message: string): UsePromptReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const confirmDialog = useConfirmDialog();
  const lastTargetHref = useRef<string | null>(null);
  
  // アプリ内の全リンクに対する処理を追加
  useEffect(() => {
    if (!when) return;
    
    // すべてのクリックイベントをキャッチ
    const handleClick = async (e: MouseEvent) => {
      // サイドバーやヘッダーの要素を含む、すべてのクリック可能な要素をチェック
      let target = e.target as HTMLElement;
      
      // プロフィールアイコンの場合は処理をスキップ（元のイベントを妨げない）
      if (target.closest('.user-profile') || 
          (target.tagName === 'svg' && 
           (target.classList.contains('user-icon') || 
            target.parentElement?.classList.contains('user-profile')))) {
        // プロフィールアイコンのクリックは処理せず、通常のイベントフローを維持
        return;
      }
      
      // クリックされた要素またはその親要素に対する処理
      while (target && target !== document.body) {
        // ナビゲーション用のデータ属性を持つ要素を検出
        const dataPath = target.getAttribute('data-path') || 
                        target.getAttribute('data-to') || 
                        target.getAttribute('data-href') || 
                        target.getAttribute('href');
        
        // ナビゲーション先パスの判定
        if (dataPath && (dataPath.startsWith('/') || dataPath.startsWith('#'))) {
          // 現在のパスと同じなら何もしない
          if (dataPath === location.pathname) return;
          
          // 同じターゲットに対する連続クリックを防止
          if (lastTargetHref.current === dataPath) return;
          lastTargetHref.current = dataPath;
          
          e.preventDefault();
          e.stopPropagation();
          
          try {
            const confirmed = await confirmDialog.confirm({ 
              message,
              title: '変更を保存せずに移動しますか？',
              confirmLabel: '移動する',
              cancelLabel: 'キャンセル'
            });
            
            if (confirmed) {
              navigate(dataPath);
            }
            
            lastTargetHref.current = null;
          } catch (error) {
            console.error('ダイアログエラー:', error);
            lastTargetHref.current = null;
          }
          
          return;
        }

        // aタグ（リンク）の場合
        if (target.tagName === 'A' && target.getAttribute('href')) {
          const href = target.getAttribute('href') || '';
          // 内部リンクのみ処理（外部リンクはスキップ）
          if (href.startsWith('/') || href.startsWith('#')) {
            // 現在のパスと同じなら何もしない
            if (href === location.pathname) return;
            
            // 同じターゲットに対する連続クリックを防止
            if (lastTargetHref.current === href) return;
            lastTargetHref.current = href;
            
            // 確認ダイアログを表示
            e.preventDefault();
            e.stopPropagation();
            
            try {
              const confirmed = await confirmDialog.confirm({ 
                message,
                title: '変更を保存せずに移動しますか？',
                confirmLabel: '移動する',
                cancelLabel: 'キャンセル'
              });
              
              if (confirmed) {
                // 確認された場合は遷移
                navigate(href);
              }
              
              // いずれの場合もリファレンスをクリア
              lastTargetHref.current = null;
            } catch (error) {
              console.error('ダイアログエラー:', error);
              lastTargetHref.current = null;
            }
            
            return;
          }
        }
        
        // SVGやアイコン要素を検出（サイドバーのアイコンなど）
        if ((target.tagName === 'svg' || target.tagName === 'IMG' || 
            target.classList?.contains('icon') || target.classList?.contains('icon-desktop')) &&
            target.closest('a[href]')) {
          const href = target.closest('a[href]')?.getAttribute('href') || '';
          
          // 内部リンクのみ処理
          if (href && (href.startsWith('/') || href.startsWith('#'))) {
            // 現在のパスと同じなら何もしない
            if (href === location.pathname) return;
            
            e.preventDefault();
            e.stopPropagation();
            
            try {
              const confirmed = await confirmDialog.confirm({ 
                message,
                title: '変更を保存せずに移動しますか？',
                confirmLabel: '移動する',
                cancelLabel: 'キャンセル'
              });
              
              if (confirmed) {
                navigate(href);
              }
            } catch (error) {
              console.error('ダイアログエラー:', error);
            }
            
            return;
          }
        }
        
        // アカウントメニュー・ボタン要素（ヘッダーのアカウントボタンなど）
        if ((target.tagName === 'BUTTON' || 
            target.getAttribute('role') === 'button') && 
            (target.textContent?.includes('アカウント') || 
            target.textContent?.includes('プロフィール') ||
            target.textContent?.includes('ログアウト'))) {
          
          e.preventDefault();
          e.stopPropagation();
          
          // 遷移先を決定
          let destination = null;
          if (target.textContent?.includes('アカウント') || target.textContent?.includes('プロフィール')) {
            destination = '/account';
          } else if (target.textContent?.includes('ログアウト')) {
            destination = '/logout';
          }
          
          if (!destination) return; // 遷移先が不明な場合は何もしない
          
          try {
            const confirmed = await confirmDialog.confirm({ 
              message,
              title: '変更を保存せずに移動しますか？',
              confirmLabel: '移動する',
              cancelLabel: 'キャンセル'
            });
            
            if (confirmed) {
              // 確認されたら、指定の遷移先へ直接遷移
              navigate(destination);
            }
          } catch (error) {
            console.error('ダイアログエラー:', error);
          }
          
          return;
        }
        
        // サイドバー内のメニュー項目
        if (target.classList && 
            (target.classList.contains('menu-item-desktop') || 
             target.classList.contains('menu-link-desktop'))) {
          
          let href = target.getAttribute('href');
          
          // href属性がない場合は子要素のaタグを探す
          if (!href) {
            const linkElement = target.querySelector('a');
            if (linkElement) {
              href = linkElement.getAttribute('href');
            }
          }
          
          if (href && (href.startsWith('/') || href.startsWith('#'))) {
            // 現在のパスと同じなら何もしない
            if (href === location.pathname) return;
            
            e.preventDefault();
            e.stopPropagation();
            
            try {
              const confirmed = await confirmDialog.confirm({ 
                message,
                title: '変更を保存せずに移動しますか？',
                confirmLabel: '移動する',
                cancelLabel: 'キャンセル'
              });
              
              if (confirmed) {
                navigate(href);
              }
            } catch (error) {
              console.error('ダイアログエラー:', error);
            }
            
            return;
          }
        }
        
        target = target.parentElement as HTMLElement;
      }
    };
    
    // キャプチャフェーズでイベントをキャッチ（バブリングより先に実行）
    document.addEventListener('click', handleClick as unknown as EventListener, true);
    
    return () => {
      document.removeEventListener('click', handleClick as unknown as EventListener, true);
    };
  }, [when, message, navigate, location.pathname]);

  const handleNavigate = useCallback(
    async (to: string) => {
      if (when) {
        try {
          const confirmed = await confirmDialog.confirm({ 
            message,
            title: '変更を保存せずに移動しますか？',
            confirmLabel: '移動する',
            cancelLabel: 'キャンセル'
          });
          
          if (confirmed) {
            navigate(to);
          }
        } catch (error) {
          console.error('ダイアログエラー:', error);
        }
      } else {
        navigate(to);
      }
    },
    [when, message, navigate]
  );

  return { handleNavigate };
};

export const useVideoEdit = (): UseVideoEditReturn => {
  
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const confirmDialog = useConfirmDialog();
  const location = useLocation();
  const [resolvedVideoId, setResolvedVideoId] = useState<string | null>(null);
  const [resolvedSessionId, setResolvedSessionId] = useState<string | null>(null);
  const isSessionId = Boolean(id && id.startsWith('session_'));
  const sessionId = isSessionId && id ? id.replace('session_', '') : null;
  const isResolvedVideoId = Boolean(resolvedVideoId && resolvedSessionId === sessionId);
  const effectiveVideoId = isResolvedVideoId ? resolvedVideoId : id;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  // 画像並べ替えモード
  const [isReorderMode, setIsReorderMode] = useState(false);
  
  // 動画基本情報
  const [video, setVideo] = useState<Video | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('private');

  // 前回のビデオIDを保持するためのref（無限ループ防止用）
  const prevVideoIdRef = useRef<string | number | null>(null);
  
  // タグ管理
  const [tags, setTags] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isComposing, setIsComposing] = useState(false);

  // 診療科関連
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState('');

  // キー付きタグ（部位:膝 など）。診療科ごとの専用入力欄はこれに置き換わった
  const [specialtyAttributes, setSpecialtyAttributes] = useState<SpecialtyAttribute[]>([]);
  // 選択中の診療科でよく使うキー（入力サジェスト用）
  const [suggestedKeys, setSuggestedKeys] = useState<string[]>([]);

  // 取得した画像を保存する状態
  const [fetchedImages, setFetchedImages] = useState<VideoImage[]>([]);

  const ensureResolvedVideoId = useCallback(() => {
    if (isSessionId && !isResolvedVideoId) {
      showSnackbar('動画IDが確定するまで操作できません。しばらくお待ちください。', SnackbarType.INFO);
      return false;
    }
    return true;
  }, [isSessionId, isResolvedVideoId, showSnackbar]);

  useEffect(() => {
    setResolvedVideoId(null);
    setResolvedSessionId(null);
  }, [id]);
  
  // 画像データをメモ化して無限ループを防ぐ
  const memoizedImages = useMemo(() => {
    const result = fetchedImages.map(img => ({
      id: img.id,
      video_id: img.video_id,
      image_path: img.image_path,
      description: img.description || '',
      display_order: img.display_order || 0,
      created_at: img.created_at || new Date().toISOString(),
      updated_at: img.updated_at || new Date().toISOString(),
    }));
    return result;
  }, [fetchedImages]);
  
  // 動画画像管理（新しいフックを使用）
  const {
    images: videoImages,
    activeImages,
    pendingDeletions,
    pendingAdditions,
    isSaving: isImageSaving,
    hasChanges: hasImageChanges,
    toggleDeletionStatus,
    isPendingDeletion,
    addImage,
    updateImage,
    removeImage,
    reorderImages,
    saveChanges: saveImageChanges,
    resetChanges: resetImageChanges,
  } = useVideoEditImages(effectiveVideoId || '', memoizedImages);
  
  // 編集状態の追跡
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [initialState, setInitialState] = useState<Record<string, string>>({});

  // レスポンシブ対応のためのハンドラー
  const handleResize = () => {
    setIsMobile(window.innerWidth <= 768);
  };
  
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // CSSカスタムプロパティを使用してフッターの高さを取得
    const setMobileFooterHeight = () => {
      const root = document.documentElement;
      const mobileFooterHeight = getComputedStyle(root).getPropertyValue('--mobile-footer-height') || '70px';
      root.style.setProperty('--video-edit-footer-space', mobileFooterHeight);
    };

    setMobileFooterHeight();
    window.addEventListener('resize', setMobileFooterHeight);
    return () => window.removeEventListener('resize', setMobileFooterHeight);
  }, []);

  useEffect(() => {
    if (!sessionId || resolvedVideoId) return;

    let isCancelled = false;
    const controller = new AbortController();
    const delayMs = 3000;
    const maxDurationMs = 20 * 60 * 1000;
    const startedAt = Date.now();

    const poll = async (): Promise<void> => {
      if (isCancelled) return;

      const activeSession = SessionManager.getActiveSession();
      if (!activeSession?.token) return;

      try {
        const response = await fetch(`/api/videos/upload/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${activeSession.token}` },
          signal: controller.signal,
        });

        if (isCancelled) return;

        if (response.ok) {
          const data = await response.json();
          if (isCancelled) return;

          if (data?.videoId) {
            setResolvedVideoId(data.videoId);
            setResolvedSessionId(sessionId);
            setVideo(prev => (prev ? { ...prev, id: data.videoId } : prev));
            return;
          }

          if (data?.status && ['failed', 'cancelled'].includes(data.status)) {
            return;
          }
        }
      } catch (error) {
        if ((error as Error).name !== 'AbortError' && !isCancelled) {
          console.error('アップロードステータス取得エラー:', error);
        }
      }

      if (isCancelled) return;

      if (Date.now() - startedAt >= maxDurationMs) {
        return;
      }

      setTimeout(poll, delayMs);
    };

    void poll();

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [sessionId, resolvedVideoId]);

  useEffect(() => {
    if (!resolvedVideoId || resolvedSessionId !== sessionId || !video) return;
    if (String(video.id).startsWith('session_')) {
      setVideo(prev => (prev ? { ...prev, id: resolvedVideoId } : prev));
    }
  }, [resolvedVideoId, resolvedSessionId, sessionId, video]);

  // React Router外部の遷移（ブラウザの戻るボタンなど）に対応
  useEffect(() => {
    // ブラウザのリロードや閉じる操作に対応
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isFormDirty) {
        const message = '変更が保存されていません。このページから移動しますか？';
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };
    
    // 遷移を試みるイベントリスナー
    const handlePopState = async (e: PopStateEvent) => {
      // 戻るボタンが押された場合
      if (isFormDirty) {
        // 現在のURLをスタックに追加して遷移を一時停止
        window.history.pushState(null, '', window.location.pathname);
        
        try {
          // カスタム確認ダイアログを表示
          const confirmed = await confirmDialog.confirm({ 
            message: '変更が保存されていません。このページから移動しますか？',
            title: '変更を保存せずに移動しますか？',
            confirmLabel: '移動する',
            cancelLabel: 'キャンセル'
          });
          
          if (confirmed) {
            // 確認されたら戻る
            window.history.back();
          }
          // キャンセルされた場合は何もしない（現在のページに留まる）
        } catch (error) {
          console.error('ダイアログエラー:', error);
        }
      }
    };

    // ブラウザ終了とリロードに対応するbeforeunloadイベントのリスナーを追加
    window.addEventListener('beforeunload', handleBeforeUnload);
    // ブラウザの戻るボタンなどに対応するpopstateイベントのリスナーを追加
    window.addEventListener('popstate', handlePopState);

    // グローバルイベントハンドラー - サイドバーとヘッダーのインタラクションを確実に検出
    const handleGlobalClicks = async (e: MouseEvent) => {
      if (!isFormDirty) return; // フォームが変更されていない場合は処理しない
      
      // プロフィールアイコンの場合は処理をスキップ（元のイベントを妨げない）
      const target = e.target as HTMLElement;
      if (target.closest('.user-profile') || 
          (target.tagName === 'svg' && 
           (target.classList.contains('user-icon') || 
            target.parentElement?.classList.contains('user-profile')))) {
        // プロフィールアイコンのクリックは処理せず、通常のイベントフローを維持
        return;
      }
      
      // イベントターゲットを取得
      let currentTarget = e.target as HTMLElement;
      
      // 確認が必要かどうかのフラグ
      let shouldConfirm = false;
      let navigationTarget = null;
      
      // 1. 特定のHTML要素をチェック
      
      // ヘッダー内の要素を検出 (Header.jsx)
      const isHeaderElement = 
        currentTarget.closest('.header') || 
        currentTarget.closest('.account-menu') ||
        (currentTarget.closest('button') && currentTarget.closest('header') && 
         (currentTarget.textContent?.includes('アカウント') || 
          currentTarget.textContent?.includes('ログアウト')));
      
      // プロフィールアイコン自体はスキップ（ドロップダウンメニュー表示のため）
      const isProfileIcon = 
        currentTarget.closest('.user-profile') || 
        (currentTarget.tagName === 'svg' && 
         (currentTarget.classList.contains('user-icon') || 
          currentTarget.parentElement?.classList.contains('user-profile')));
      
      // サイドバー内の要素を検出 (Sidebar.jsx)
      const isSidebarElement = 
        currentTarget.closest('.sidebar-desktop') || 
        currentTarget.closest('.menu-desktop') || 
        currentTarget.closest('.menu-item-desktop') || 
        currentTarget.closest('.menu-link-desktop');
      
      // 2. React Router Linkを検出 (React Routerが使用するDOM属性)
      const isReactRouterLink = currentTarget.closest('a[href^="/"]');
      
      // 3. クリックされた要素の検証
      while (currentTarget && currentTarget !== document.body) {
        // アカウントメニュー関連のクリック検出 (Header.jsx)
        if (currentTarget.tagName === 'BUTTON' && 
            ((currentTarget.textContent || '').includes('アカウント') || 
             (currentTarget.textContent || '').includes('ログアウト'))) {
          shouldConfirm = true;
          navigationTarget = (currentTarget.textContent || '').includes('アカウント') ? '/account' : '/logout';
          break;
        }
        
        // FaUserCircleアイコンはスキップ（ドロップダウンメニュー表示のため）
        if (currentTarget.tagName === 'svg' && 
            (currentTarget.classList.contains('user-icon') || 
             currentTarget.parentElement?.classList.contains('user-profile'))) {
          // プロフィールアイコンのクリックは確認ダイアログを表示しない
          return; // 早期リターンで処理を終了
        }
        
        // ドロップダウンメニュー内の項目を検出
        if (currentTarget.tagName === 'LI' && currentTarget.closest('.account-menu') && 
            ((currentTarget.textContent || '').includes('アカウント') || 
             (currentTarget.textContent || '').includes('ログアウト'))) {
          shouldConfirm = true;
          navigationTarget = (currentTarget.textContent || '').includes('アカウント') ? '/account' : '/logout';
          break;
        }
        
        // ドロップダウンメニュー内のAタグを検出
        if (currentTarget.tagName === 'A' && currentTarget.closest('.account-menu')) {
          shouldConfirm = true;
          navigationTarget = currentTarget.getAttribute('href');
          break;
        }
        
        // サイドバーのリンクを検出 (Sidebar.jsx)
        if (currentTarget.tagName === 'A' && 
            (currentTarget.classList.contains('menu-link-desktop') || 
             currentTarget.closest('.menu-link-desktop'))) {
          shouldConfirm = true;
          navigationTarget = currentTarget.getAttribute('href');
          break;
        }
        
        // React Router Linkを使用している場合
        if (currentTarget.tagName === 'A' && currentTarget.getAttribute('href')?.startsWith('/')) {
          shouldConfirm = true;
          navigationTarget = currentTarget.getAttribute('href');
          break;
        }
        
        // アイコンの親要素がLinkコンポーネントの場合（サイドバーのアイコン）
        if (currentTarget.tagName === 'svg' && 
            currentTarget.closest('a[href]')) {
          shouldConfirm = true;
          navigationTarget = currentTarget.closest('a[href]')?.getAttribute('href') || null;
          break;
        }
        
        currentTarget = currentTarget.parentElement as HTMLElement;
      }
      
      // プロフィールアイコンのクリックはスキップ
      if (isProfileIcon) {
        return;
      }
      
      // 確認ダイアログを表示する必要がある場合
      if ((shouldConfirm || (isHeaderElement && !isProfileIcon) || isSidebarElement || isReactRouterLink) && 
         !e.defaultPrevented) {
        e.preventDefault();
        e.stopPropagation();
        
        try {
          // すでに処理中の場合は重複して確認しない
          if ((window as any).__confirmingNavigation) return;
          (window as any).__confirmingNavigation = true;
          
          const confirmed = await confirmDialog.confirm({ 
            message: '変更が保存されていません。このページから移動しますか？',
            title: '変更を保存せずに移動しますか？',
            confirmLabel: '移動する',
            cancelLabel: 'キャンセル'
          });
          
          (window as any).__confirmingNavigation = false;
          
          if (confirmed) {
            // 確認された場合、NavigateをページURIに基づいて実行
            if (navigationTarget) {
              navigate(navigationTarget);
            } else {
              // ターゲットが不明な場合、元のクリックをシミュレート
              const clickEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              (e.target as HTMLElement).dispatchEvent(clickEvent);
            }
          }
        } catch (error) {
          console.error('ダイアログエラー:', error);
          (window as any).__confirmingNavigation = false;
        }
      }
    };
    
    // キャプチャフェーズでグローバルクリックを検出 (バブリングより先に実行)
    document.addEventListener('click', handleGlobalClicks as unknown as EventListener, { capture: true });
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('click', handleGlobalClicks as unknown as EventListener, { capture: true });
    };
  }, [isFormDirty, confirmDialog, navigate]);

  useEffect(() => {
    const getVideo = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const apiVideoData = await fetchVideoById(id);
        
        // APIから返されたデータをコンポーネントの型に変換
        const videoData = mapApiVideoToVideo(apiVideoData);
        
        setVideo(videoData);
        setTitle(videoData.title);
        setDescription(videoData.description || '');
        setVisibility(videoData.visibility || 'private');

        // タグ初期化
        if (videoData.tags) {
          const tagArray = videoData.tags
            .split(',')
            .map((t: string) => t.trim())
            .filter(Boolean);
          setTags(tagArray);
        }

        // 診療科ID
        if (videoData.specialty) {
          setSelectedSpecialtyId(videoData.specialty.id);
        } else {
          setSelectedSpecialtyId('');
        }

        // キー付きタグ。キーか値が空・「未設定」の行は落として初期値にする
        const initialAttributes = resolveSpecialtyAttributes(videoData);
        setSpecialtyAttributes(initialAttributes);

        // 初期状態を保存（編集検知用）
        setInitialState({
          title: videoData.title,
          description: videoData.description || '',
          visibility: videoData.visibility || 'private',
          tags: videoData.tags || '',
          selectedSpecialtyId: videoData.specialty ? videoData.specialty.id : '',
          specialtyAttributes: JSON.stringify(initialAttributes),
        });
        setIsFormDirty(false);
      } catch (err) {
        showSnackbar('動画情報の取得に失敗しました。', SnackbarType.ERROR);
      } finally {
        setLoading(false);
      }
    };

    const getSpecialties = async () => {
      try {
        const specialtyList = await fetchSpecialties();
        // APIから返された診療科データをコンポーネントの型に変換
        setSpecialties(mapApiSpecialtiesToSpecialties(specialtyList));
      } catch (err) {
        showSnackbar('診療科の取得に失敗', SnackbarType.ERROR);
      }
    };

    getVideo();
    getSpecialties();
  }, [id]);

  // 診療科が変わったら、その科でよく使うキーの候補を取り直す（無くても入力はできる）
  useEffect(() => {
    if (!selectedSpecialtyId) {
      setSuggestedKeys([]);
      return;
    }

    let isCancelled = false;
    fetchSpecialtyAttributeKeys(selectedSpecialtyId)
      .then(keys => {
        if (!isCancelled) setSuggestedKeys(keys);
      })
      .catch(() => {
        // 候補が取れなくても入力はできるので、エラー表示はしない
        if (!isCancelled) setSuggestedKeys([]);
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedSpecialtyId]);

  // 動画画像の取得（動画情報取得後に実施）
  useEffect(() => {
    if (!video) {
      return;
    }
    
    const videoId = String(video.id);
    
    // 既に同じIDの画像が取得済みの場合はスキップ
    if (fetchedImages.length > 0 && fetchedImages[0]?.video_id === videoId) {
      return;
    }
    
    // 画像変更中（hasImageChangesがtrue）の場合はスキップ
    if (hasImageChanges) {
      return;
    }
    
    // video.idが実際に変わった場合のみ実行
    const getVideoImages = async () => {
      try {
        const data = await fetchVideoImages(videoId);
        
        // APIレスポンスの検証
        if (!data) {
          return;
        }
        
        // APIレスポンスが配列でない場合の対応
        if (!Array.isArray(data)) {
          // data.videoImagesがある場合（旧API形式）
          const dataObj = data as Record<string, any>;
          if (dataObj.videoImages && Array.isArray(dataObj.videoImages)) {
            const convertedImages = dataObj.videoImages.map((img: any) => ({
              id: String(img.id || ''),
              video_id: String(img.video_id || ''),
              image_path: img.image_path || '',
              description: img.description || '',
              display_order: img.display_order || 0,
              created_at: img.created_at || '',
              updated_at: img.updated_at || ''
            }));
            // 取得した画像を状態に設定（重複を防ぐ）
            setFetchedImages(prev => {
              // 既存の画像と同じIDの画像がある場合は更新しない
              if (prev.length === convertedImages.length && 
                  prev.every((existing, index) => existing.id === convertedImages[index]?.id)) {
                return prev;
              }
              return convertedImages;
            });
          } else {
            // 空の配列を設定してエラーを防ぐ
            setFetchedImages([]);
          }
          return;
        }
        
        // 型変換: APIの型を内部型にマッピング
        const convertedImages = data.map(img => ({
          id: String(img.id),
          video_id: String(img.video_id), 
          image_path: img.image_path,
          description: img.description || '',
          display_order: img.position || 0,
          created_at: img.created_at,
          updated_at: img.updated_at
        }));
        
        // 取得した画像を状態に設定（重複を防ぐ）
        setFetchedImages(prev => {
          // 既存の画像と同じIDの画像がある場合は更新しない
          if (prev.length === convertedImages.length && 
              prev.every((existing, index) => existing.id === convertedImages[index]?.id)) {
            return prev;
          }
          return convertedImages;
        });
      } catch (err) {
        showSnackbar('動画画像の取得に失敗', SnackbarType.ERROR);
      }
    };
    
    getVideoImages();
    
    // video.idを依存配列に追加し、videoオブジェクト全体ではなくIDの変更時のみ実行
  }, [video?.id, hasImageChanges]);
  
  // フォームの変更を監視して編集状態を更新
  useEffect(() => {
    if (!initialState.title) {
      return; // 初期値がロードされるまで何もしない
    }
    
    const currentState = {
      title,
      description,
      visibility,
      tags: tags.join(','),
      selectedSpecialtyId,
      specialtyAttributes: JSON.stringify(specialtyAttributes),
    };

    // 初期状態と現在の状態を比較
    const isDirty = Object.keys(initialState).some(key => {
      return initialState[key] !== (currentState as Record<string, string>)[key];
    });

    // 現在のisFormDirtyと異なる場合のみ更新（不要な再レンダリングを防ぐ）
    setIsFormDirty(prevIsFormDirty => {
      if (prevIsFormDirty !== isDirty) {
        return isDirty;
      }
      return prevIsFormDirty;
    });
  }, [title, description, visibility, tags, selectedSpecialtyId, specialtyAttributes, initialState.title, initialState.description, initialState.visibility, initialState.tags, initialState.selectedSpecialtyId, initialState.specialtyAttributes]);

  // 診療科変更時のハンドラ。タグは科をまたいでも使えるので消さない
  const handleSpecialtyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSpecialtyId(e.target.value);
  };

  // BMP 変換中フラグ。変換の await 中に次の追加を受け付けると、上限枚数の判定が古い画像数で行われるため
  const isConvertingImagesRef = useRef(false);

  // 画像ファイル選択 => 即時アップロード（複数対応）
  const handleImageFileChange = async (
    fileOrEvent: React.ChangeEvent<HTMLInputElement> | File | FileList | File[]
  ) => {
    if (!effectiveVideoId) return;
    if (!ensureResolvedVideoId()) return;
    
    const normalizeFiles = (
      input: React.ChangeEvent<HTMLInputElement> | File | FileList | File[]
    ): File[] => {
      if (input instanceof File) {
        return [input];
      }

      if (Array.isArray(input)) {
        return input.filter((file): file is File => file instanceof File);
      }

      if ('target' in input && input.target?.files) {
        return Array.from(input.target.files);
      }

      const fileListCandidate = input as FileList;
      if (typeof fileListCandidate?.length === 'number' && typeof fileListCandidate?.item === 'function') {
        return Array.from(fileListCandidate);
      }

      return [];
    };

    let files = normalizeFiles(fileOrEvent);
    if (files.length === 0) {
      console.error('無効なパラメータ:', fileOrEvent);
      showSnackbar('画像の処理に失敗しました', SnackbarType.ERROR);
      return;
    }

    const allowedTypes = new Set(['image/jpeg', 'image/jpg', 'image/png']);
    const validFiles = files.filter(file => allowedTypes.has(file.type) || isBmpFile(file));
    if (validFiles.length === 0) {
      showSnackbar('JPG・PNG・BMP形式のファイルのみアップロードできます。', SnackbarType.WARNING);
      return;
    }
    if (validFiles.length !== files.length) {
      showSnackbar('JPG・PNG・BMP形式のファイルのみアップロードできます。', SnackbarType.WARNING);
    }
    files = validFiles;

    if (isConvertingImagesRef.current) {
      showSnackbar('画像を変換中です。完了してから追加してください。', SnackbarType.WARNING);
      return;
    }

    // 削除予定を除いた既存画像・保存予定の画像の数を計算
    const currentActiveImageCount = activeImages.filter(img => {
      const isPendingDeletionFlag = isPendingDeletion(img.id);
      return !isPendingDeletionFlag;
    }).length;
    
    const maxImageCount = 5;
    const remainingSlots = maxImageCount - currentActiveImageCount;
    
    if (remainingSlots <= 0) {
      showSnackbar('削除予定を除いて1動画につき登録可能な画像は最大5枚です', SnackbarType.WARNING);
      return;
    }
    
    if (files.length > remainingSlots) {
      showSnackbar(
        `登録済みを含めて最大${maxImageCount}枚までのため、残り${remainingSlots}枚だけ追加します`,
        SnackbarType.WARNING
      );
      files = files.slice(0, remainingSlots);
    }
    
    // 一意のタイムスタンプを生成 (ミリ秒単位)
    const timestamp = Date.now();
    
    // 進捗状況をユーザーに表示するためのカウンター
    if (files.length > 1) {
      showSnackbar(`${files.length}枚の画像をアップロード中...`, SnackbarType.INFO);
    }
    
    isConvertingImagesRef.current = files.some(isBmpFile);
    try {
      // ファイルごとに1つずつアップロード（同時実行ではなく順次処理）
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          // BMP はサーバーで受け付けないため PNG に変換してから保持する
          const uploadFile = isBmpFile(file) ? await convertBmpToPng(file) : file;

          // 一時的な画像オブジェクトを作成（保存時に正式にアップロード）
          const tempImage = {
            id: `temp_${timestamp}_${i}_${Math.random().toString(36).substr(2, 9)}`,
            video_id: String(effectiveVideoId),
            image_path: '', // 一時的な画像なので空
            description: '',
            display_order: videoImages.length + i,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            file: uploadFile // ファイルオブジェクトを保持
          };

          // 一時的な画像を追加
          addImage(tempImage);
        } catch (err) {
          console.error(`[ERROR] 画像アップロードエラー (${i+1}/${files.length}):`, err);
          showSnackbar(`画像「${file.name}」のアップロードに失敗しました`, SnackbarType.ERROR);
        }
      }
    } finally {
      isConvertingImagesRef.current = false;
    }
    
    if (files.length > 1) {
      showSnackbar(`${files.length}枚の画像をアップロードしました`, SnackbarType.SUCCESS);
    }
  };

  // 画像削除予定の切り替え（新しいフロー）
  const handleDeleteImage = (imageId: string | number) => {
    if (!ensureResolvedVideoId()) return;
    const stringId = String(imageId);
    toggleDeletionStatus(stringId);
  };

  // 動画情報保存
  const handleSave = async () => {
    if (!effectiveVideoId) return;
    if (!ensureResolvedVideoId()) return;

    // 非公開から公開範囲を広げるときは診療科が必須。サーバーでも弾くが、ここで先に案内する
    if (requiresSpecialtyForVisibilityChange(initialState.visibility || 'private', visibility, selectedSpecialtyId)) {
      showSnackbar(PUBLISH_REQUIRES_SPECIALTY_MESSAGE, SnackbarType.WARNING);
      setActiveTab('tags');
      return;
    }

    try {

      // 1. 画像の変更を保存（削除予定と新規追加を一括処理）
      if (hasImageChanges) {
        const imageResult = await saveImageChanges();
        if (!imageResult.success) {
          throw new Error('画像の保存に失敗しました');
        }
      }

      // 2. 動画情報を保存
      const tagsString = tags.join(',');
      const payload: VideoUpdateReq = {
        title,
        description,
        visibility: visibility as VideoUpdateReq['visibility'],
        tags: tagsString,
        specialty_id: selectedSpecialtyId || '',
        specialty_attributes: normalizeSpecialtyAttributes(specialtyAttributes)
      };

      // 動画情報を保存
      await updateVideo(effectiveVideoId, payload);
      
      // 成功した場合は、メッセージなしでマイビデオ画面に遷移
      showSnackbar('動画情報が保存されました。', SnackbarType.SUCCESS);
      
      // 保存後にフォームの状態をリセット
      setIsFormDirty(false);
      
      // マイビデオ画面に遷移
      navigate('/my-videos');
    } catch (err) {
      // 失敗した場合はエラーメッセージを表示
      console.error('動画情報保存エラー:', err);
      
      // エラーメッセージを抽出
      let errorMessage = '動画情報の保存に失敗しました。';
      
      if (err && typeof err === 'object') {
        // Axiosエラーの場合
        if ('response' in err && err.response) {
          const response = err.response as any;
          if (response.data?.error) {
            errorMessage = response.data.error;
          } else if (response.data?.message) {
            errorMessage = response.data.message;
          }
        }
        // 一般的なErrorオブジェクトの場合
        else if ('message' in err) {
          errorMessage = (err as Error).message;
        }
      }
      
      showSnackbar(errorMessage, SnackbarType.ERROR);
    }
  };

  // 画像並べ替えモードの状態変更を処理するコールバック
  const handleReorderModeChange = (mode: boolean) => {
    setIsReorderMode(mode);
  };

  // 画像削除処理のコールバック（VideoEditImagesSectionから受け取る）
  const [commitPendingDeletions, setCommitPendingDeletions] = useState<(() => Promise<void>) | null>(null);
  // 一時的な画像を正式に紐付ける関数の状態（新しいフローでは不要）
  // const [commitTempImages, setCommitTempImages] = useState<(() => Promise<any[]>) | null>(null);

  return {
    id,
    video,
    title,
    setTitle,
    description,
    setDescription,
    visibility,
    setVisibility,
    tags,
    setTags,
    inputValue,
    setInputValue,
    isComposing,
    setIsComposing,
    specialties,
    selectedSpecialtyId,
    handleSpecialtyChange,
    specialtyAttributes,
    setSpecialtyAttributes,
    suggestedKeys,
    videoImages,
    activeImages,
    pendingDeletions,
    pendingAdditions,
    isImageSaving,
    hasImageChanges,
    isPendingDeletion,
    toggleDeletionStatus,
    removeImage,
    loading,
    isMobile,
    isReorderMode,
    isFormDirty,
    activeTab,
    setActiveTab,
    handleImageFileChange,
    handleDeleteImage,
    handleSave,
    handleReorderModeChange,
    setCommitPendingDeletions,
    updateImageOrder: (newOrderedImages: VideoImage[]) => {
      // 新しい順序でfetchedImagesを更新
      setFetchedImages(newOrderedImages);
    },
    navigate
  };
}; 
